import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Boxes, CheckCircle2, XCircle, Settings, RefreshCw, Loader2 } from "lucide-react";

export default function AdminServicesPanel() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [svc, setSvc] = useState({ services: {}, defs: [] });
  const [savingSvc, setSavingSvc] = useState(false);
  const setFlag = (key, field, val) => setSvc((c) => ({ ...c, services: { ...c.services, [key]: { ...c.services[key], [field]: val } } }));
  const saveSvc = async () => {
    setSavingSvc(true);
    try { const { data } = await api.put("/admin/service-config", { services: svc.services }); setSvc(data); toast.success("Disponibilità servizi salvata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio"); }
    finally { setSavingSvc(false); }
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/services/status").then((r) => setProviders(r.data.providers || [])).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/service-config").then((r) => setSvc(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const importAll = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/admin/catalog/import/all");
      toast.success(`Servizi OpenAPI sincronizzati (${data.total}). Airalo e Reloadly sono live e non usano il catalogo.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore import"); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="services-panel-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Amministrazione</p>
          <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Boxes className="w-7 h-7 text-primary" /> Pannello Servizi</h1>
          <p className="text-sm text-muted-foreground mt-1">Stato di connessione e configurazione di tutti i fornitori e servizi.</p>
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Caricamento…</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {providers.map((p) => (
            <Card key={p.key} className="glow-border p-5 flex flex-col gap-3 relative" data-testid={`service-card-${p.key}`}>
              <Badge className={`absolute top-4 right-4 whitespace-nowrap ${p.configured ? "bg-teal-600 hover:bg-teal-600" : ""}`} variant={p.configured ? undefined : "outline"} data-testid={`service-status-${p.key}`}>
                {p.configured ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                {p.configured ? "Configurato" : "Da configurare"}
              </Badge>
              <h2 className="font-head font-semibold text-base leading-tight pr-32 min-h-[2.5rem]">{p.label}</h2>
              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                {p.mode && <Badge variant="outline" className="uppercase">{p.mode}</Badge>}
                {p.products != null && <Badge variant="outline">{p.products} prodotti</Badge>}
                {p.packages != null && <Badge variant="outline">{p.packages} pacchetti</Badge>}
                {p.services_enabled != null && <Badge variant="outline">{p.services_enabled} servizi attivi</Badge>}
              </div>
              <div className="mt-auto space-y-2">
                {p.key === "openapi" && <Button onClick={importAll} disabled={importing} variant="secondary" size="sm" className="w-full gap-2" data-testid="panel-import-all-button">
                  {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Sincronizzo…</> : <><RefreshCw className="w-4 h-4" /> Sincronizza servizi OpenAPI</>}
                </Button>}
                <NavLink to={`/app/${p.config_route}`} className="block">
                  <Button variant="outline" size="sm" className="w-full gap-2" data-testid={`service-config-${p.key}`}>
                    <Settings className="w-4 h-4" /> Configura
                  </Button>
                </NavLink>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card className="glow-border p-5 space-y-4" data-testid="service-availability-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-head text-lg font-semibold">Disponibilità servizi</h2>
            <p className="text-xs text-muted-foreground mt-0.5">"Live" = visibile e vendibile da dealer/master/clienti. "Manutenzione" = nascosto a tutti (tranne admin). L'admin vede sempre tutto.</p>
          </div>
          <Button onClick={saveSvc} disabled={savingSvc} data-testid="service-availability-save">{savingSvc ? "Salvataggio…" : "Salva disponibilità"}</Button>
        </div>
        <div className="divide-y divide-border">
          {(svc.defs || []).map((d) => {
            const s = svc.services?.[d.key] || { live: true, maintenance: false };
            return (
              <div key={d.key} className="flex flex-wrap items-center justify-between gap-4 py-2.5" data-testid={`svc-row-${d.key}`}>
                <span className="text-sm font-medium">{d.label}
                  {s.maintenance ? <Badge variant="destructive" className="ml-2 text-[10px]">In manutenzione</Badge>
                    : s.live ? <Badge className="ml-2 text-[10px] bg-teal-600 hover:bg-teal-600">Live</Badge>
                    : <Badge variant="outline" className="ml-2 text-[10px]">Non live</Badge>}
                </span>
                <div className="flex flex-wrap items-center gap-5 min-w-0">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">Live
                    <Switch checked={!!s.live} onCheckedChange={(v) => setFlag(d.key, "live", v)} data-testid={`svc-live-${d.key}`} />
                  </label>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">Manutenzione
                    <Switch checked={!!s.maintenance} onCheckedChange={(v) => setFlag(d.key, "maintenance", v)} data-testid={`svc-maint-${d.key}`} />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
